import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Switch, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import * as constantes from "../../../common/Constantes";
import ConfigUtils from "../../../utils/Config";
import moment from "moment";
import { IFacturaMetodoPago } from "../facturarCita/FacturarCitaDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Utilities from "../../../common/Utilities";
import IPaciente from "../../../types/Paciente";
import { getCurrencySymbol } from "../../../utils/Factura";
import PacienteService from "../../../services/PacienteService";
import { useNavigate } from "react-router-dom";
import IAlert from "../../../types/Alert";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import FacturacionService from "../../../services/FacturacionService";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import { IFacturaManualPayload } from "../../../utils/Factura";
import SelectorEntidadFacturacion, { esFacturacionMultiEntidad } from "./SelectorEntidadFacturacion";

export interface IFacturaManualErrors {
  paciente?: string;
  servicioName?: string;
  concepto?: string;
  precio?: string;
  metodoPago?: string;
  fechaPago?: string;
  entidad?: string;
  general?: string;
}

interface ICrearFacturaManualFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (factura: IFacturaCompleta) => void;
}

const CrearFacturaManualForm = ({ open, onClose, onSuccess}: ICrearFacturaManualFormProps) => {
    const simboloMoneda = getCurrencySymbol(ConfigUtils.getUserCurrency());
    const history = useNavigate();
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState<IFacturaManualErrors>({});

    const [pacientes, setPacientes] = useState<IPaciente[]>([]);
    const [paciente, setPaciente] = useState<IPaciente | null>(null);
    const [nombre, setNombre] = useState<string>("");
    const [concepto, setConcepto] = useState<string>("");
    const [precio, setPrecio] = useState<number>(0);
    const [impuesto, setImpuesto] = useState<number>(0);
    const [precioTotal, setPrecioTotal] = useState(0);
    const [inputPrecioBase, setInputPrecioBase] = useState("0,00");
    const [facturaPagada, setFacturaPagada] = useState<boolean>(false);
    const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
    const [loadingAction, setLoadingAction] = useState(false);
    const [selectedEntidadId, setSelectedEntidadId] = useState<string | null | undefined>(undefined);

    const metodosPago = ConfigUtils.getMetodosPago();

    const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

    const load = async () => {
        try {
          const response = await PacienteService.getAll();
          setPacientes(response.data);
        } catch (e: any) {
          if (e.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
          } else {
            const error: ILog = {
              method: "CrearFacturaForm.Component.getPacientes",
              level: "error",
              message: e.message,
              meta: e.response,
            };
            LogService.logError(error);
            setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
          }
        } finally {
          setLoading(false);
        }
    };

    useEffect(() => {
      load();
    }, []);
    
    const validateFactura = (): IFacturaManualErrors => {
        const errors: IFacturaManualErrors = {};

        if (esFacturacionMultiEntidad() && selectedEntidadId === undefined) {
          errors.entidad = "Debes seleccionar una entidad de facturación";
        }
        if (!paciente) errors.paciente = "Debes seleccionar un receptor para la factura";
        if (!nombre.trim()) errors.servicioName = "El nombre del servicio es obligatorio";
        if (!concepto.trim()) errors.concepto = "El concepto es obligatorio";
        if (precio <= 0) errors.precio = "El precio debe ser mayor que 0";

        if (facturaPagada) {
          if (!metodoPago) {
            errors.metodoPago = "Debes seleccionar un método de pago";
          } else {
            if (!metodoPago.fechaPago) {
              errors.fechaPago = "Debes seleccionar una fecha de pago";
            } else if (
              !moment.isMoment(metodoPago.fechaPago) ||
              !metodoPago.fechaPago.isValid()
            ) {
              errors.fechaPago = "Debes seleccionar una fecha de pago válida";
            }
          }
        }

        return errors;
    }

    const crearFactura = async () => {

      const validationErrors = validateFactura();
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length > 0) return;

      const precioFinal = Number(precio.toFixed(2));

      const nuevaFactura: IFacturaManualPayload = {
        userId: ConfigUtils.getUserId(),
        pacienteId: paciente?._id!,
        servicio: nombre,
        concepto,
        precio: precioFinal,
        impuesto,
        metodoPago,
        entityId: selectedEntidadId
      };

      setLoadingAction(true);

      try {
        const response = await FacturacionService.createFacturaManual(nuevaFactura);
        onSuccess(response.data.factura);
        onClose();
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "CrearFacturaForm.Component.createFactura",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingAction(false);
      }

    };

    return (
        <>
        <Dialog open={open} maxWidth="sm" fullWidth onClose={loadingAction ? undefined : onClose}>
            <DialogTitle>
                Crear factura
            </DialogTitle>
            {loading ? <CircularProgress style={{ margin: "20px auto" }} /> : 
            <DialogContent dividers>
              <>
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <SelectorEntidadFacturacion
                    value={selectedEntidadId}
                    onChange={(v) => { setSelectedEntidadId(v); setErrors((prev: IFacturaManualErrors) => ({ ...prev, entidad: undefined })); }}
                    error={!!errors.entidad}
                  />
                </Grid>
                {/* RECEPTOR */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Autocomplete
                    disablePortal
                    autoComplete
                    id="pacientes"
                    options={pacientes}
                    value={paciente}
                    onChange={(_, v) => {setPaciente(v!); setErrors((prev: IFacturaManualErrors) => ({ ...prev, paciente: undefined })) }}
                    getOptionLabel={(option) => `${option.nombre} ${option.apellidos}` || ""}
                    noOptionsText={'No se han encontrado pacientes'}
                    renderOption={(props, option) => { return (<li {...props} key={option._id}>{`${option.nombre} ${option.apellidos}`}</li>) }}
                    renderInput={(params) => (
                      <TextField {...params} label="Receptor de la factura*" error={!!errors.paciente} helperText={errors.paciente} />)}
                    filterOptions={(options, params) => Utilities.filterPacientesAutocomplete(options, params.inputValue)}
                  />
                </Grid>
                <Grid container spacing={{ xs: 0, md: 2 }}>
                    <Grid item xs={12} md={4}>
                      {/* Servicio */}
                      <TextField
                        label="Servicio*"
                        disabled={loading}
                        fullWidth
                        sx={{ mb: 2 }}
                        value={nombre}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          setErrors((prev: IFacturaManualErrors) => ({ ...prev, servicioName: undefined }));
                        }}
                        error={!!errors.servicioName}
                        helperText={errors.servicioName}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                  {/* Concepto */}
                  <TextField
                    label="Concepto*"
                    disabled={loading}
                    fullWidth
                    sx={{ mb: 2 }}
                    value={concepto}
                    onChange={(e) => {
                      setConcepto(e.target.value);
                      setErrors((prev: IFacturaManualErrors) => ({ ...prev, concepto: undefined }));
                    }}
                    error={!!errors.concepto}
                    helperText={errors.concepto}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={{ xs: 0, md: 2 }}>
                <Grid item xs={12} md={4}>
                  {/* Precio */}
                  <TextField
                    label={`Precio base (${simboloMoneda})*`}
                    disabled={loading}
                    fullWidth
                    type="text"
                    sx={{ mb: 2 }}
                    value={inputPrecioBase}
                    onChange={(e) => {
                      let raw = e.target.value;
                      setInputPrecioBase(raw);

                      const normalized = raw.replace(",", ".");
                      if (/^\d*([.,]\d*)?$/.test(raw)) {
                        const nuevoPrecio = parseFloat(normalized);
                    
                        if (!isNaN(nuevoPrecio)) {
                          setPrecio(nuevoPrecio);
                          setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
                          setErrors((prev: IFacturaManualErrors) => ({ ...prev, precio: undefined }))
                        } else {
                          setPrecio(0);
                          setPrecioTotal(0);
                        }
                      }
                    }}
                    onBlur={() => {
                      const formatted = precio.toFixed(2).replace(".", ",");
                      setInputPrecioBase(formatted);
                    }}
                    error={!!errors.precio}
                    helperText={errors.precio}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  {/* Impuesto */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
                    <Select
                      labelId="select-impuesto-label"
                      disabled={loading}
                      value={impuesto}
                      label="Impuesto (%)*"
                      onChange={(e) => {
                        const nuevoImp = Number(e.target.value);
                        setImpuesto(nuevoImp);
                        setPrecioTotal(precio + (precio * nuevoImp) / 100);
                      }}
                    >
                      {constantes.IMPUESTOS.map((imp) => (
                        <MenuItem key={imp} value={imp}>
                          {imp}%
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={`Precio total (${simboloMoneda})`}
                    value={precioTotal.toFixed(2).replace(".", ",")}
                    InputProps={{ readOnly: true, sx: {
                      backgroundColor: '#f0f0f0',
                      color: '#555',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#bdbdbd'
                      }
                    }}}
                  />
                </Grid>
              </Grid>

                {/* Bloque de Pago */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                >
                  {/* Texto explicativo */}
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ flexBasis: "100%", flexGrow: 1 }}
                  >
                    * Si la factura se marca como pagada, el método de pago es obligatorio.
                  </Typography>

                  {/* Switch de factura pagada */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={facturaPagada}
                        disabled={loading}
                        onChange={(e) => {
                          const pagada = e.target.checked;

                          setFacturaPagada(pagada);
                          setMetodoPago(undefined);
                        
                          setErrors((prev: IFacturaManualErrors) => ({
                            ...prev,
                            metodoPago: undefined,
                          }));
                        }}
                      />
                    }
                    label="Factura pagada"
                    sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
                  />

                  {/* Select de método de pago */}
                  {facturaPagada && (
                    <Grid container spacing={2} alignItems="flex-start">
                      {/* Método de pago */}
                      <Grid item xs={12} sm={6} md={6}>
                        <FormControl fullWidth 
                        error={!!errors.metodoPago}
                        >
                          <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                          <Select
                            labelId="metodo-pago-label"
                            disabled={loading}
                            value={metodoPago?.nombre || ""}
                            label="Método de pago*"
                            onChange={(e) => {
                              const value = e.target.value;
                              const metodo = metodosPago.find((m) => m.nombre === value)!;
                                setMetodoPago({
                                  nombre: metodo.nombre,
                                  codigo: metodo.codigo,
                                  fechaPago: metodoPago?.fechaPago || moment(),
                                });
                            
                              setErrors((prev: IFacturaManualErrors) => ({ ...prev, metodoPago: undefined }));
                            }}
                          >
                            {metodosPago.filter((m) => m.activo).map((m) => (
                              <MenuItem key={m.codigo} value={m.nombre}>
                                {m.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.metodoPago && (
                            <Typography color="error" variant="caption">
                              {errors.metodoPago}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      
                      {/* Fecha de pago */}
                      <Grid item xs={12} sm={6} md={6}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            label="Fecha de pago"
                            value={metodoPago?.fechaPago}
                            disabled={loading}
                            onChange={(date) => {
                              if (metodoPago) {
                                setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                                setErrors((prev: IFacturaManualErrors) => ({ ...prev, fechaPago: undefined }));
                              }
                            }}
                            renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} 
                              /> )}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </>
            </DialogContent>}
            <DialogActions>
                <Button onClick={crearFactura} disabled={loadingAction}>Guardar</Button>
                <Button onClick={onClose} disabled={loadingAction}>Cancelar</Button>
            </DialogActions>
        </Dialog>

        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>
        </>
    );
};

export default CrearFacturaManualForm;